import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header, { ARAC_TOOLS } from "../components/Header";
import Footer from "../components/Footer";
import VenueCard from "../components/VenueCard";
import WeatherTool from "../components/WeatherTool";
import AgendaTool from "../components/AgendaTool";
import BudgetTool from "../components/BudgetTool";
import SeatingTool from "../components/SeatingTool";
import GuestListTool from "../components/GuestListTool";
import InvitationTool from "../components/InvitationTool";
import WelcomeTour from "../components/WelcomeTour";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import { useMember } from "../context/MemberContext";
import { getVenue } from "../mock/mockData";
import { Heart, Mail, Star, User, Loader2, LogOut, Save, Inbox, Wrench, ArrowRight, ChevronDown } from "lucide-react";

const TABS = [
  { id: "araclar", label: "Araçlar", icon: Wrench },
  { id: "favoriler", label: "Favorilerim", icon: Heart },
  { id: "teklifler", label: "Tekliflerim", icon: Mail },
  { id: "yorumlar", label: "Yorumlarım", icon: Star },
  { id: "profil", label: "Profil", icon: User },
];

const MemberPanel = () => {
  const { member, token, loading, API, logout, updateProfile } = useMember();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("araclar");
  const [aracTab, setAracTab] = useState("davetli-listem");
  const [aracOpen, setAracOpen] = useState(true);
  const [favVenues, setFavVenues] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pName, setPName] = useState("");
  const [pPass, setPPass] = useState("");
  const [savingP, setSavingP] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [banners, setBanners] = useState([]);
  const [shuffled, setShuffled] = useState([]);
  const [shuffleSecs, setShuffleSecs] = useState(10);
  const viewedBanners = React.useRef(new Set());

  useEffect(() => {
    if (!member) return;
    axios.get(`${API}/member-banners`, { params: member.city ? { city: member.city } : {} }).then((r) => setBanners(r.data || [])).catch(() => {});
    axios.get(`${API}/banner-settings`).then((r) => setShuffleSecs(Number(r.data?.shuffleSeconds) || 10)).catch(() => {});
  }, [API, member]);

  const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  useEffect(() => {
    setShuffled(banners.length > 3 ? shuffle(banners) : banners);
    if (banners.length <= 3) return;
    const id = setInterval(() => setShuffled(shuffle(banners)), Math.max(1, shuffleSecs) * 1000);
    return () => clearInterval(id);
  }, [banners, shuffleSecs]);

  useEffect(() => {
    (shuffled.slice(0, 3)).forEach((b) => {
      if (b && !viewedBanners.current.has(b.id)) {
        viewedBanners.current.add(b.id);
        axios.post(`${API}/member-banners/${b.id}/view`).catch(() => {});
      }
    });
  }, [shuffled, API]);

  useEffect(() => {
    if (!loading && member && sessionStorage.getItem("ds_new_member")) {
      sessionStorage.removeItem("ds_new_member");
      setWelcomeOpen(true);
    }
  }, [loading, member]);

  useEffect(() => { if (!loading && !member) navigate("/"); }, [loading, member, navigate]);
  useEffect(() => { if (member) setPName(member.name || ""); }, [member]);

  const authH = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!member) return;
    (async () => {
      const slugs = member.favorites || [];
      const resolved = await Promise.all(slugs.map(async (s) => {
        const v = getVenue(s);
        if (v) return v;
        try { const r = await axios.get(`${API}/firms/${s}`); return r.data; } catch { return null; }
      }));
      setFavVenues(resolved.filter(Boolean));
    })();
  }, [member, API]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/member/quotes`, { headers: authH }).then((r) => setQuotes(r.data || [])).catch(() => {});
    axios.get(`${API}/member/reviews`, { headers: authH }).then((r) => setReviews(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [token]);

  const saveProfile = async () => {
    setSavingP(true);
    try {
      await updateProfile({ name: pName, ...(pPass ? { password: pPass } : {}) });
      setPPass("");
      toast({ title: "Kaydedildi", description: "Profiliniz güncellendi." });
    } catch { toast({ title: "Hata", description: "Güncellenemedi." }); }
    finally { setSavingP(false); }
  };

  const ARAC_SUBTABS = ARAC_TOOLS.filter((t) => t.key !== "favorilerim");
  const renderTool = (key) => {
    switch (key) {
      case "web-sitem": return <InvitationTool />;
      case "ajandam": return <AgendaTool />;
      case "butcem": return <BudgetTool />;
      case "oturma-plani": return <SeatingTool />;
      case "davetli-listem": return <GuestListTool />;
      case "hava-durumu": return <WeatherTool />;
      default: {
        const t = ARAC_TOOLS.find((x) => x.key === key) || ARAC_TOOLS[0];
        const I = t.icon;
        return (
          <div className="bg-white rounded-2xl border border-[#F0DDE0] py-20 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center mx-auto mb-5">
              <I className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#2A2224]">{t.label}</h1>
            <p className="text-[#7C6E70] mt-3 max-w-md mx-auto">{t.desc}</p>
            <span className="inline-block mt-5 px-4 py-1.5 rounded-full bg-[#FBF3E4] text-[#8a6d1f] text-sm font-medium">Çok yakında</span>
          </div>
        );
      }
    }
  };

  if (loading || !member) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FBF5F0]"><Loader2 className="w-8 h-8 animate-spin text-[#9E2A3B]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FBF5F0]">
      <Header />
      <WelcomeTour open={welcomeOpen} onClose={() => setWelcomeOpen(false)} memberName={member.name} />
      <div className="max-w-7xl mx-auto px-5 pt-28 pb-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center text-white font-display text-lg">
              {(member.name || "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="font-display text-2xl text-[#2A2224]" data-testid="member-name">Merhaba, {member.name}</h1>
              <p className="text-sm text-[#7C6E70]">{member.phone}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="member-logout"
            className="flex items-center gap-2 px-4 h-10 rounded-xl border border-[#F0DDE0] text-[#9E2A3B] text-sm hover:bg-[#FBEEF0] transition-colors">
            <LogOut className="w-4 h-4" /> Çıkış Yap
          </button>
        </div>

        {banners.length > 0 && (() => {
          const visible = (banners.length > 3 ? shuffled : banners).slice(0, 3);
          return (
            <div className="mb-6" data-testid="member-banner-area">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {visible.map((b) => {
                  const src = b.image?.startsWith("http") ? b.image : `${process.env.REACT_APP_BACKEND_URL}${b.image}`;
                  const inner = <img src={src} alt={b.title || "reklam"} className="w-full h-full object-cover" />;
                  return (
                    <div key={b.id} className="relative rounded-2xl overflow-hidden border border-[#F0DDE0] bg-white shadow-sm aspect-[4/3]" data-testid={`member-banner-${b.id}`}>
                      {b.link ? (
                        <a href={b.link} target="_blank" rel="noreferrer" data-testid="member-banner-link" onClick={() => axios.post(`${API}/member-banners/${b.id}/click`).catch(() => {})} className="block w-full h-full">{inner}</a>
                      ) : inner}
                      <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 rounded bg-black/40 text-white">Reklam</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
          <nav className="flex lg:flex-col gap-2 overflow-x-auto">
            {TABS.map((t) => {
              const I = t.icon;
              const active = tab === t.id;
              return (
                <React.Fragment key={t.id}>
                  <button onClick={() => { if (t.id === "araclar") { if (tab === "araclar") { setAracOpen((v) => !v); } else { setTab("araclar"); setAracOpen(true); } } else { setTab(t.id); } }} data-testid={`member-tab-${t.id}`}
                    className={`flex items-center gap-2.5 shrink-0 px-4 py-2.5 rounded-xl text-sm font-medium text-left transition-all ${active ? "bg-[#9E2A3B] text-white shadow-sm" : "bg-white text-[#5b4d4f] hover:bg-[#FBF0F2]"}`}>
                    <I className="w-4 h-4" /> {t.label}
                    {t.id === "araclar" && <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${active && aracOpen ? "rotate-180" : ""}`} />}
                    {t.id === "favoriler" && favVenues.length > 0 && <span className={`ml-auto text-xs px-1.5 rounded-full ${active ? "bg-white/25" : "bg-[#FBEEF0] text-[#9E2A3B]"}`}>{favVenues.length}</span>}
                  </button>
                  {t.id === "araclar" && active && aracOpen && (
                    <div className="flex lg:flex-col gap-1 lg:pl-3 lg:ml-2 lg:border-l lg:border-[#F0DDE0] mb-1" data-testid="member-arac-subtabs">
                      {ARAC_SUBTABS.map((s) => {
                        const SI = s.icon;
                        const son = aracTab === s.key;
                        return (
                          <button key={s.key} onClick={() => setAracTab(s.key)} data-testid={`member-arac-subtab-${s.key}`}
                            className={`flex items-center gap-2 shrink-0 px-3 py-2 rounded-lg text-sm text-left transition-all ${son ? "bg-[#FBEEF0] text-[#9E2A3B] font-medium" : "text-[#7C6E70] hover:bg-[#FBF5F0]"}`}>
                            <SI className="w-3.5 h-3.5" /> {s.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          <div className="min-w-0">
            {tab === "araclar" && (
              <div data-testid="member-araclar">
                {renderTool(aracTab)}
              </div>
            )}

            {tab === "favoriler" && (
              <div data-testid="member-favorites">
                {favVenues.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-[#F0DDE0] p-12 text-center text-[#7C6E70]">
                    <Heart className="w-10 h-10 mx-auto mb-3 text-[#E7A9B0]" />
                    Henüz favori eklemediniz. Mekan sayfalarındaki <Heart className="w-3.5 h-3.5 inline text-[#9E2A3B]" /> ikonuna tıklayarak favorilerinize ekleyebilirsiniz.
                    <div className="mt-4"><Link to="/kategori/dugun-salonlari" className="text-[#9E2A3B] font-medium hover:underline">Mekanları keşfet →</Link></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                    {favVenues.map((v) => <VenueCard key={v.slug || v.id} venue={v} />)}
                  </div>
                )}
              </div>
            )}

            {tab === "teklifler" && (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="member-quotes">
                {quotes.length === 0 ? (
                  <div className="p-12 text-center text-[#7C6E70]"><Inbox className="w-10 h-10 mx-auto mb-3 text-[#E7A9B0]" /> Henüz teklif talebi göndermediniz.</div>
                ) : (
                  <ul className="divide-y divide-[#F0DDE0]">
                    {quotes.map((q) => (
                      <li key={q.id} className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="font-medium text-[#2A2224]">{q.firm_name}</span>
                          <span className="text-xs text-[#a8999b]">{q.at ? new Date(q.at).toLocaleString("tr-TR") : ""}</span>
                        </div>
                        {q.message && <p className="text-sm text-[#5b4d4f] mt-1">{q.message}</p>}
                        {q.date && <span className="text-xs text-[#7C6E70]">Etkinlik tarihi: {q.date}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === "yorumlar" && (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="member-reviews">
                {reviews.length === 0 ? (
                  <div className="p-12 text-center text-[#7C6E70]"><Star className="w-10 h-10 mx-auto mb-3 text-[#E7A9B0]" /> Henüz yorum yazmadınız.</div>
                ) : (
                  <ul className="divide-y divide-[#F0DDE0]">
                    {reviews.map((r) => (
                      <li key={r.id} className="p-4">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <span className="font-medium text-[#2A2224]">{r.firm_name || "Mekan"}</span>
                          <span className="flex items-center gap-0.5">
                            {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "text-[#C6A15B]" : "text-[#E3D6D8]"}`} fill={n <= r.rating ? "#C6A15B" : "#E3D6D8"} />)}
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-[#E7F7EE] text-[#1FA855]" : "bg-[#FBF3E4] text-[#8a6d1f]"}`}>{r.status === "approved" ? "Yayında" : "Onay bekliyor"}</span>
                          </span>
                        </div>
                        <p className="text-sm text-[#5b4d4f] mt-1">{r.comment}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {tab === "profil" && (
              <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 max-w-lg" data-testid="member-profile">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#7C6E70]">Ad Soyad</label>
                    <Input value={pName} onChange={(e) => setPName(e.target.value)} data-testid="member-profile-name" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#7C6E70]">Telefon (değiştirilemez)</label>
                    <Input value={member.phone} disabled className="bg-[#FBF5F0] border-[#F0DDE0]" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm text-[#7C6E70]">Yeni Şifre (opsiyonel)</label>
                    <Input type="password" value={pPass} onChange={(e) => setPPass(e.target.value)} data-testid="member-profile-password" placeholder="Değiştirmek istemiyorsanız boş bırakın" className="border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                  <button onClick={saveProfile} disabled={savingP} data-testid="member-profile-save"
                    className="flex items-center gap-2 px-5 h-10 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white text-sm font-medium hover:shadow-md transition-all disabled:opacity-70">
                    {savingP ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Kaydet
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MemberPanel;
